import * as Dialog from "@radix-ui/react-dialog";
import { useState, type ReactNode } from "react";

interface DangerDialogProps {
  trigger?: ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** The literal string the user must type to enable the confirm button. */
  confirmPhrase: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => Promise<void> | void;
  testid?: string;
}

/**
 * GitHub-style "type the name to confirm" dialog. Reserved for irreversible
 * destructive actions where a single click confirmation isn't enough — e.g.
 * permanent user delete, "burn this branch" patterns.
 */
export function DangerDialog({
  trigger,
  defaultOpen = false,
  onOpenChange: externalOnOpenChange,
  title,
  description,
  confirmPhrase,
  confirmLabel,
  cancelLabel = "Abbrechen",
  onConfirm,
  testid,
}: DangerDialogProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [pending, setPending] = useState(false);
  const [typed, setTyped] = useState("");

  const armed = typed === confirmPhrase && !pending;

  function setBoth(next: boolean) {
    setOpen(next);
    if (!next) setTyped("");
    externalOnOpenChange?.(next);
  }

  async function handleConfirm() {
    if (!armed) return;
    setPending(true);
    try {
      await onConfirm();
      setBoth(false);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={setBoth}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          data-testid={testid ?? "danger-dialog"}
          className="
            fixed z-50 bg-elevated shadow-modal p-5 space-y-4 border border-danger/40
            left-0 right-0 bottom-0 rounded-t-sheet animate-sheet-up
            sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:right-auto sm:w-[460px]
            sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-sheet sm:animate-slide-up
            safe-bottom sm:pb-5
          "
        >
          <div className="sm:hidden mx-auto -mt-1 mb-2 h-1.5 w-10 rounded-full bg-outline" aria-hidden />
          <Dialog.Title className="text-lg font-bold text-danger">{title}</Dialog.Title>
          <Dialog.Description className="text-sm text-fg-muted">
            {description}
          </Dialog.Description>
          <label className="block space-y-1.5">
            {/*
              Label stays uppercase ("BESTÄTIGUNG: TIPPE") to match the rest
              of the dialog's chrome, BUT the inner <code> gets
              `normal-case` so the actual phrase the user must type (e.g.
              an email address) shows in its real case. Without that
              override the parent's uppercase bleeds into the <code> and
              the user thinks they need to type in CAPS — they can't, the
              string compare is case-sensitive against the literal phrase,
              and the button stays disabled.
            */}
            <span className="text-xs uppercase tracking-wider text-fg-faint">
              Bestätigung: tippe{" "}
              <code className="text-fg bg-bg/40 px-1 rounded font-mono select-all normal-case">
                {confirmPhrase}
              </code>
            </span>
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid="danger-confirm-input"
              className="w-full min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg font-mono focus:outline-none focus:border-danger focus:ring-2 focus:ring-danger/30"
              placeholder={confirmPhrase}
            />
          </label>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={pending}
                data-testid="danger-cancel"
                className="min-h-[48px] rounded-xl bg-bg/40 border border-outline text-fg text-sm font-semibold hover:bg-card disabled:opacity-60"
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={!armed}
              data-testid="danger-confirm"
              className="min-h-[48px] rounded-xl bg-danger text-accent-on text-sm font-semibold hover:bg-danger/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pending ? "…" : confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
